import { useEffect } from "react";
import { Controller, FieldArrayWithId, useForm, useFormContext, useWatch } from "react-hook-form";

// Redux
import { useDispatch } from "react-redux";
import { getCompanyAll, saveCompanyAll, useCompanyReducer } from "@/main/store/company";
import { getCompanyType, useCompanyTypeReducer } from "@/main/store/companyType";

// Services
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";

// Types
import { KeyValue } from "@/main/models/common";
import { DynamicReportForm, DynamicReportPermissionForm } from "../../../registerDynamicReport";
import { hasSameUserGroups } from "../../../utilRegisterDynamicReport";

// Api

// Components
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import { Checkbox, FormControlLabel } from "@mui/material";
import UserGroupAutoComplete from "./userGroupAutoComplete/userGroupAutoComplete";

import * as S from "./styles";

interface INewPermissionProps {
	fields: FieldArrayWithId<DynamicReportForm, "permissions", "id">[];
	permissionToEdit: DynamicReportPermissionForm;
	addNew: (newPermission: DynamicReportPermissionForm) => void;
	finishEdit: (editedPermission: DynamicReportPermissionForm | null) => void;
}

function NewPermission({ fields, permissionToEdit, addNew, finishEdit }: INewPermissionProps) {
	const dispatch = useDispatch();

	const { companyAll: companyReducer } = useCompanyReducer();
	const { companyType: companyTypeReducer } = useCompanyTypeReducer();

	const isEdit = !!permissionToEdit;
	const INITIAL_FORM_STATE: DynamicReportPermissionForm = {
		guidDynamicReportPermission: null,
		companyType: null,
		company: null,
		allCompanies: false,
		userGroups: [],
	};
	const methods = useForm<DynamicReportPermissionForm>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
	});

	const {
		getValues,
		setValue,
		reset,
		resetField,
		trigger,
		formState: { isValid },
		control,
	} = methods;

	const { watch } = useFormContext<DynamicReportForm>();
	const watcherCountry = watch("country");

	const watcherCompanyType = useWatch({
		control,
		name: "companyType",
	});

	const watcherCompany = useWatch({
		control,
		name: "company",
	});

	const watcherAllCompanies = useWatch({
		control,
		name: "allCompanies",
	});
	const isRequiredCompany = !watcherAllCompanies;

	function clearCopmanies() {
		dispatch(saveCompanyAll([], false, []));
	}

	async function callCompanyApiDomain(companyTypeSelected: KeyValue | null) {
		resetField("company");
		resetField("userGroups");

		if (isNullUndefined(companyTypeSelected) || isNullUndefined(watcherCountry)) {
			clearCopmanies();
			return;
		}

		dispatch(
			getCompanyAll({
				guidCountry: String(watcherCountry.key),
				idCompanyType: String(companyTypeSelected.key),
				idStatusRegister: StatusRegisterEnum.Active,
			}),
		);
	}

	useEffect(() => {
		dispatch(getCompanyType());
	}, []);

	useEffect(() => {
		if (isEdit) {
			const value = permissionToEdit;
			setValue("companyType", value.companyType);
			callCompanyApiDomain(value.companyType);
			setValue("company", value.company);
			setValue("allCompanies", value.allCompanies);
			setValue("userGroups", value.userGroups);
			setValue("guidDynamicReportPermission", value.guidDynamicReportPermission);
		}
	}, [permissionToEdit]);

	useEffect(() => {
		if (getValues("companyType")) trigger("company");
	}, [watcherAllCompanies]);

	function SaveToList() {
		if (!isValid) return trigger();
		const data = getValues();
		if (!isEdit) {
			addNew(data);
		} else {
			finishEdit(data);
		}
		reset(INITIAL_FORM_STATE);
		clearCopmanies();
	}

	function cancelEdit() {
		reset(INITIAL_FORM_STATE);
		clearCopmanies();
		finishEdit(null);
	}

	function validateExistingPermissions(company: KeyValue) {
		const allCompanies = getValues("allCompanies");
		const companyType = getValues("companyType");
		const userGroups = getValues("userGroups");
		if (companyType == null) {
			return true;
		}

		if (
			permissionToEdit &&
			permissionToEdit.companyType?.key == companyType.key &&
			permissionToEdit.company?.key == company?.key &&
			permissionToEdit.allCompanies == allCompanies &&
			hasSameUserGroups(permissionToEdit.userGroups, userGroups)
		) {
			return true;
		}

		const existingField = fields.find(
			(existingField) =>
				existingField.companyType?.key == companyType.key &&
				existingField.company?.key == company?.key &&
				existingField.allCompanies == allCompanies &&
				hasSameUserGroups(existingField.userGroups, userGroups),
		);
		if (existingField) {
			return getTranslateByKey("information.PermissionsRegistered", true);
		}

		return true;
	}

	return (
		<>
			<S.NewPermissionArea>
				<KogAutoCompleteControlled
					label={getTranslateByKey("information.companyType")}
					nameCustom="companyType"
					control={control}
					rulesCustom={{ required: true }}
					options={companyTypeReducer.data}
					isLoading={companyTypeReducer.isLoading}
					onChangeCustom={(value) => {
						callCompanyApiDomain(value);
					}}
					id="id-newPermission-companyType"
				/>

				<S.ContainerCopmanySelect $isNullUndefinedValue={isNullUndefined(watcherCompany)}>
					<KogAutoCompleteControlled
						id="id-newPermission-company"
						nameCustom="company"
						disableClearable={isNullUndefined(watcherCompany)}
						label={getTranslateByKey("information.company")}
						control={control}
						setValueCustom={setValue}
						options={companyReducer.data}
						isLoading={companyReducer.isLoading}
						rulesCustom={{
							required: isRequiredCompany,
							validate: validateExistingPermissions,
						}}
						onChangeCustom={(value) => {
							resetField("userGroups");
							if (value) {
								setValue("allCompanies", false);
							}
						}}
					/>
					{isNullUndefined(watcherCompany) && (
						<Controller
							name="allCompanies"
							control={control}
							render={({ field: { onChange, value } }) => (
								<S.AreaCheckboxAllCopmanies>
									<FormControlLabel
										label={getTranslateByKey("information.all")}
										control={
											<Checkbox
												color="primary"
												checked={value}
												onChange={(e) => {
													onChange(e.target.checked);
												}}
												id="id-newPermission-checkbox-allcompanies"
											/>
										}
									/>
								</S.AreaCheckboxAllCopmanies>
							)}
						/>
					)}
				</S.ContainerCopmanySelect>
				<S.UserGroupArea>
					<UserGroupAutoComplete
						control={control}
						companyType={watcherCompanyType}
						company={watcherCompany}
						country={watcherCountry}
						onChangeCustom={() => trigger("company")}
					/>
				</S.UserGroupArea>
			</S.NewPermissionArea>
			<S.AreaButton>
				<SecondaryButton onClick={SaveToList}>
					{isEdit ? getTranslateByKey("information.edit") : getTranslateByKey("dynamicReportPermission.addPermission")}
				</SecondaryButton>
				{isEdit && <SecondaryButton onClick={cancelEdit}>{getTranslateByKey("information.cancel")}</SecondaryButton>}
			</S.AreaButton>
		</>
	);
}

export default NewPermission;
